"use client";

import { useEffect, useRef, useState } from "react";
import { useRouter } from "next/navigation";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { QrisPaymentStep } from "@/components/payments/qris-payment-step";
import { PaymentSuccessView } from "@/components/payments/payment-success-view";
import { useI18n } from "@/components/i18n-provider";
import { getGa4ClientIdFromCookie } from "@/lib/ga-client-id";
import {
  createPaymentIdempotencyKey,
  PAYMENT_IDEMPOTENCY_KEY_HEADER,
} from "@/lib/payments/payment-idempotency";
import { cn } from "@/lib/utils";
import { getCareerBundleActivationPath } from "@/lib/payments/career-bundle-destination";

const AUTO_REDIRECT_MS = 4500;

interface QrData {
  paymentId: string;
  qrString: string | null;
  invoiceUrl: string | null;
  amount: number;
}

export function CareerBundleCheckoutDialog({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const router = useRouter();
  const { locale, dictionary } = useI18n();
  const copy = dictionary.dashboard.offerPage.bundle;

  const [isStarting, setIsStarting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [qrData, setQrData] = useState<QrData | null>(null);
  const [isComplete, setIsComplete] = useState(false);
  const redirectTimer = useRef<number | null>(null);
  const isStartingRef = useRef(false);
  const activeIdempotencyKeyRef = useRef<string | null>(null);

  const clearRedirect = () => {
    if (redirectTimer.current !== null) {
      window.clearTimeout(redirectTimer.current);
      redirectTimer.current = null;
    }
  };

  useEffect(() => clearRedirect, []);

  const reset = () => {
    clearRedirect();
    setIsStarting(false);
    setError(null);
    setQrData(null);
    setIsComplete(false);
    if (!isStartingRef.current) {
      activeIdempotencyKeyRef.current = null;
    }
  };

  const handleOpenChange = (next: boolean) => {
    if (!next) {
      reset();
    }
    onOpenChange(next);
  };

  const goToBundleActivation = () => {
    clearRedirect();
    router.push(getCareerBundleActivationPath(locale));
  };

  const startCheckout = async () => {
    if (isStartingRef.current) {
      return;
    }

    isStartingRef.current = true;
    setIsStarting(true);
    setError(null);

    try {
      const gaClientId = getGa4ClientIdFromCookie();
      const idempotencyKey =
        activeIdempotencyKeyRef.current ?? createPaymentIdempotencyKey();
      activeIdempotencyKeyRef.current = idempotencyKey;
      const response = await fetch("/api/career-os/booster", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          [PAYMENT_IDEMPOTENCY_KEY_HEADER]: idempotencyKey,
        },
        body: JSON.stringify(gaClientId ? { gaClientId } : {}),
      });

      const result = await response.json().catch(() => ({}));

      if (!response.ok) {
        throw new Error(
          typeof result?.error === "string" ? result.error : copy.error,
        );
      }

      const paymentId: string | undefined =
        result?.paymentId ?? result?.payment?.id;

      if (!paymentId) {
        throw new Error(copy.error);
      }

      activeIdempotencyKeyRef.current = null;
      setQrData({
        paymentId,
        qrString: result?.qrString ?? null,
        invoiceUrl: result?.invoiceUrl ?? null,
        amount: Number(result?.amount ?? 0),
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : copy.error);
    } finally {
      isStartingRef.current = false;
      setIsStarting(false);
    }
  };

  const handleSuccess = () => {
    setIsComplete(true);
    // payment_success is recorded server-side once the payment is
    // authoritatively confirmed, not from this client callback.
    toast.success(copy.successToast);
    clearRedirect();
    redirectTimer.current = window.setTimeout(() => {
      router.push(getCareerBundleActivationPath(locale));
    }, AUTO_REDIRECT_MS);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent
        closeLabel={copy.close}
        className={cn(
          "max-h-[calc(100dvh-2rem)] min-w-0 overflow-x-hidden overflow-y-auto sm:max-w-md",
          qrData && !isComplete && "sm:max-w-2xl",
        )}
      >
        <DialogHeader className={isComplete ? "sr-only" : undefined}>
          <DialogTitle>
            {isComplete ? copy.successTitle : copy.checkoutTitle}
          </DialogTitle>
          <DialogDescription>
            {isComplete ? copy.successBody : copy.tagline}
          </DialogDescription>
        </DialogHeader>

        {isComplete ? (
          <PaymentSuccessView
            title={copy.successTitle}
            body={copy.successBody}
            ctaLabel={copy.successCta}
            onContinue={goToBundleActivation}
            redirectingLabel={copy.successRedirecting}
          />
        ) : qrData ? (
          <QrisPaymentStep
            paymentId={qrData.paymentId}
            qrString={qrData.qrString}
            invoiceUrl={qrData.invoiceUrl}
            amount={qrData.amount}
            labels={dictionary.payment.qris}
            layout="split"
            onSuccess={handleSuccess}
            onRetry={reset}
          />
        ) : (
          <div className="space-y-4">
            {error ? (
              <p className="text-sm text-status-error-text" role="alert">
                {error}
              </p>
            ) : null}
            <Button
              type="button"
              onClick={startCheckout}
              disabled={isStarting}
              className="min-h-11 w-full gap-2 bg-brand-primary text-white hover:bg-brand-primary-hover"
            >
              {isStarting ? (
                <>
                  <Loader2
                    className="h-4 w-4 animate-spin motion-reduce:animate-none"
                    aria-hidden="true"
                  />
                  {copy.starting}
                </>
              ) : (
                copy.start
              )}
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
